import { Component } from "@theme/component";
import {
  ThemeEvents,
  VariantUpdateEvent,
  ZoomMediaSelectedEvent,
} from "@theme/events";

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGalleryComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest(".shopify-section, dialog");

    target?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate,
      { signal },
    );
    this.refs.zoomDialogComponent?.addEventListener(
      ThemeEvents.zoomMediaSelected,
      this.#handleZoomMediaSelected,
      {
        signal,
      },
    );
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector("media-gallery-component");

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * Can be called as:
   * - /zoom/INDEX → zoom that index
   * - /zoom      → zoom the currently selected slide
   *
   * @param {number|Event} input - The index of the media to zoom, or the event when no index is passed.
   * @param {PointerEvent} [event] - The pointer event.
   */
  zoom(input, event) {
    const coercedIndex = Number(input);
    const index = Number.isNaN(coercedIndex)
      ? this.slideshow?.current ?? 0
      : coercedIndex;

    const carouselDialog = this.refs.carouselDialogComponent;
    if (carouselDialog) {
      carouselDialog.open(index, event);
      return;
    }

    // Fallback to the zoom dialog behavior
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Opens the fullscreen slideshow dialog starting from the current slide.
   *
   * Can be called as:
   * - /openSlideshowDialog/INDEX → open at that index
   * - /openSlideshowDialog      → open at the currently selected slide
   *
   * @param {number|Event} input - The index of the media to show, or the event when no index is passed.
   * @param {PointerEvent} [event] - The pointer event.
   */
  openSlideshowDialog(input, event) {
    const carouselDialog = this.refs.carouselDialogComponent;
    if (!carouselDialog) {
      // When this public method is called, we expect the custom fullscreen carousel dialog
      // to be present in the DOM. Throw so we can capture it in Sentry.
      throw new Error(
        "media-gallery-component: carousel-dialog-component ref missing in openSlideshowDialog().",
      );
    }

    // When the binding is `/openSlideshowDialog` (no index), the first argument
    // will be the click event; in that case we open at the current slide.
    const evt = input instanceof Event ? input : event;
    const coercedIndex = Number(input);
    const index = input instanceof Event
      ? this.slideshow?.current ?? 0
      : Number.isNaN(coercedIndex)
        ? this.slideshow?.current ?? 0
        : coercedIndex;

    carouselDialog.open(index, evt);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get("media-gallery-component")) {
  customElements.define("media-gallery-component", MediaGalleryComponent);
}
